import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import {
  useCreateNewProperty,
  useGetCompanyDetails,
} from '@/modules/settings/company/apiHooks';
import { useCompanySettingsUtils } from '@/modules/settings/company/components/logic';
import { AddPropertyBasicInfo } from '@/modules/settings/company/components/Properties/Modals/AddProperty/BasicInfo';
import CreatePropertySuccess from '@/modules/settings/company/components/Properties/Modals/Success';
import { ICompanySettings } from '@/modules/settings/company/types';
import { Button } from '@/shared/components/atoms';
import { useAuth } from '@/shared/hooks/context/auth';

const AddProperty = () => {
  const { t } = useTranslation();

  const { addPropertyValidationSchema } = useCompanySettingsUtils();

  const { user } = useAuth();

  const formMethods = useForm<ICompanySettings.IAddPropertyFormValues>({
    resolver: yupResolver(addPropertyValidationSchema),
    mode: 'all',
  });

  const { data } = useGetCompanyDetails(user?.companyId);

  useEffect(() => {
    if (data && user?.companyId)
      formMethods.reset(
        {
          companyId: user.companyId,
          country: data.country,
          timeZone: '',
          currency: null,
        },
        { keepDefaultValues: true }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { mutate, isSuccess, data: newProperty } = useCreateNewProperty();

  const queryClient = useQueryClient();

  const onSubmit: SubmitHandler<ICompanySettings.IAddPropertyFormValues> = (
    values
  ) => {
    mutate(values as ICompanySettings.IAddPropertyDTO, {
      onSuccess: () => {
        queryClient.invalidateQueries(['getProperties']);
        queryClient.invalidateQueries(['getEntities']);
      },
    });
  };

  return isSuccess ? (
    <CreatePropertySuccess entityId={newProperty.entityId} />
  ) : (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <div className='mb-24 min-w-[700px] border-b-xs border-neutralVariant-90 pb-24'>
          <AddPropertyBasicInfo />
        </div>
        {/* <AddPropertyCrop /> */}
        <div className='mt-32 flex w-full justify-end'>
          <Button type='submit'>{t('confirm')}</Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddProperty;
